import { useEffect, useState } from "react";
import { api, resolveMedia } from "../lib/api";
import { openWhatsApp } from "../lib/whatsapp";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { X, Users, Clock, Check } from "lucide-react";
import { InkPaloma, InkFlourish } from "../components/Ink";

export default function Experiences() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/experiences").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const reserve = (x) => openWhatsApp(`Hola. Me gustaría reservar la experiencia "${x.title}". ¿Podemos coordinar fecha?`);

  return (
    <div className="bg-white" data-testid="experiences-page">
      {/* HEADER — Cream canvas */}
      <section className="canvas-cream py-20 sm:py-24 relative overflow-hidden">
        <InkPaloma className="hidden md:block absolute top-14 right-20 w-40 h-24 opacity-70" stroke="#1F3A6E" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p className="eyebrow">Experiencias · Colección Privada</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-6xl leading-[1.02] text-cobalt-deep max-w-4xl">Pensadas para su casa. <span className="serif-italic text-cobalt">Coordinadas por nosotros.</span></h1>
          <p className="mt-6 text-[1rem] text-muted2 leading-relaxed max-w-2xl">
            Una colección editada de experiencias privadas para hacer del fin de semana algo memorable, sin salir de la propiedad. Se agenda con dos semanas de anticipación.
          </p>
          <InkFlourish className="w-40 h-5 mt-8 opacity-70" />
        </div>
      </section>

      {/* GRID — white */}
      <section className="bg-white max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {items.map((x, i) => (
            <button
              key={x.id}
              onClick={() => setSelected(x)}
              className="group text-left card-hover fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`experience-${x.id}`}
            >
              <div className={`overflow-hidden aspect-[4/5] ${i === 0 ? 'bg-cobalt' : ''}`}>
                <img src={resolveMedia(x.image)} alt={x.title} className={`card-img w-full h-full object-cover ${i === 0 ? 'duotone-cobalt' : ''}`} />
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="eyebrow">N° {String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-serif text-2xl leading-snug text-ink group-hover:text-cobalt transition-colors">{x.title}</h3>
                  <p className="mt-2 text-[0.9rem] text-muted2 leading-snug">{x.tagline}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-[0.58rem] tracking-[0.28em] uppercase text-subtle font-display font-semibold">Desde</p>
                  <p className="mt-1 font-serif text-lg text-ink">{x.price_from}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA — Cobalt canvas */}
      <section className="canvas-cobalt py-20 text-center">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <p className="eyebrow-on-dark">¿Otra idea en mente?</p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-white leading-tight">Si no la encuentra en la lista, <span className="serif-italic" style={{ color: "#E88A5A" }}>escríbanos.</span></h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">Muchas de nuestras experiencias nacieron de una petición particular. Cuéntenos qué tienen en mente.</p>
          <a href="/concierge" className="btn-outline-cream mt-8 inline-flex" data-testid="exp-to-concierge">Hablar con el concierge</a>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white border border-cobalt/40 p-0 rounded-none max-h-[90vh] overflow-y-auto" data-testid="experience-dialog">
          {selected && (
            <div>
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 z-10 bg-white/90 p-2 hover:bg-white" aria-label="Cerrar" data-testid="exp-close">
                <X size={18} />
              </button>
              <img src={resolveMedia(selected.image)} alt={selected.title} className="w-full h-72 sm:h-96 object-cover" />
              <div className="p-8 sm:p-10">
                <p className="eyebrow">Experiencia Privada</p>
                <DialogTitle className="mt-3 font-serif text-3xl sm:text-4xl text-ink leading-tight">{selected.title}</DialogTitle>
                <p className="mt-3 script text-2xl">{selected.tagline}</p>
                <p className="mt-6 text-[1rem] text-muted2 leading-relaxed">{selected.description}</p>
                <div className="mt-8 grid grid-cols-2 gap-6 border-y border-cobalt/40 py-6">
                  <div>
                    <p className="eyebrow">Duración</p>
                    <p className="mt-2 text-ink inline-flex items-center gap-2"><Clock size={16}/> {selected.duration}</p>
                  </div>
                  <div>
                    <p className="eyebrow">Grupo</p>
                    <p className="mt-2 text-ink inline-flex items-center gap-2"><Users size={16}/> {selected.group_size}</p>
                  </div>
                </div>
                <div className="mt-8">
                  <p className="eyebrow">Qué incluye</p>
                  <ul className="mt-4 space-y-2">
                    {selected.includes.map((inc, i) => (
                      <li key={i} className="flex items-start gap-3 text-muted2"><Check size={16} className="mt-1 text-cobalt shrink-0"/> {inc}</li>
                    ))}
                  </ul>
                </div>
                <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-cobalt/40 pt-6">
                  <div>
                    <p className="eyebrow">Desde</p>
                    <p className="font-serif text-2xl text-ink mt-1">{selected.price_from}</p>
                  </div>
                  <button className="btn-primary" onClick={() => reserve(selected)} data-testid="exp-reserve">Reservar por WhatsApp</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
